import React, { useContext, useEffect, useState } from 'react';
import { LangContext } from '../contexts/langContext';
import { StateContext } from '../contexts/stateContext';
import { API_URL } from '../apiUrl';
import { Logo } from './UiKit/Logo';
import {
  AUTH_MODAL_FORGOT,
  AUTH_MODAL_LOGIN,
  AUTH_MODAL_SIGNUP,
  appleAuthHref,
  googleAuthHref,
  postAuth,
} from './utilsAuth';

const GoogleMark = () => (
  <svg className="auth_google_ico" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.58 2.68-3.9 2.68-6.62z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.85.86-3.04.86-2.34 0-4.32-1.58-5.03-3.71H.96v2.33A8.99 8.99 0 0 0 9 18z" />
    <path fill="#FBBC05" d="M3.97 10.71A5.4 5.4 0 0 1 3.69 9c0-.59.1-1.17.28-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.46.35 2.83.96 4.04l3.01-2.33z" />
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A8.99 8.99 0 0 0 .96 4.96l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
  </svg>
);

const AppleMark = () => (
  <svg className="auth_google_ico" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#111" d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zm3.378-3.066c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.56-1.702z" />
  </svg>
);

export function AuthModal() {
  const context = { ...useContext(StateContext), ...useContext(LangContext) };
  const $ui = context.$ui;
  const mode = context.state.authModal;
  const setMode = context.setState.setAuthModal;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (!mode) return undefined;
    document.body.classList.add('noscroll');
    const onKey = (event) => {
      if (event.key === 'Escape') setMode(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('noscroll');
      window.removeEventListener('keydown', onKey);
    };
  }, [mode, setMode]);

  useEffect(() => {
    setError('');
    setBusy(false);
    setForgotSent(false);
    setPassword('');
    setPasswordRepeat('');
  }, [mode]);

  if (!mode) return null;

  const isSignup = mode === AUTH_MODAL_SIGNUP;
  const isForgot = mode === AUTH_MODAL_FORGOT;
  const title = isSignup
    ? $ui('auth_tab_signup')
    : isForgot
      ? $ui('auth_forgot_title')
      : $ui('auth_brand');

  const close = () => setMode(false);

  const submit = async (event) => {
    event.preventDefault();
    setError('');

    if (!email.trim()) {
      setError($ui('auth_error_required'));
      return;
    }

    if (isForgot) {
      setBusy(true);
      try {
        await postAuth('/auth0/forgot', { email: email.trim() });
        setForgotSent(true);
      } catch (err) {
        setError(err.message || $ui('auth_error_generic'));
      } finally {
        setBusy(false);
      }
      return;
    }

    if (!password) {
      setError($ui('auth_error_required'));
      return;
    }

    if (isSignup && password !== passwordRepeat) {
      setError($ui('auth_error_mismatch'));
      return;
    }

    setBusy(true);
    try {
      const data = await postAuth(isSignup ? '/auth0/signup' : '/auth0/password', {
        email: email.trim(),
        password,
      });
      window.location.href = data.complete || `${API_URL}/`;
    } catch (err) {
      setError(err.message || $ui('auth_error_generic'));
      setBusy(false);
    }
  };

  return (
    <div className="auth_modal">
      <div className="auth_modal_backdrop" onClick={close} />
      <div className="auth_card" role="dialog" aria-modal="true" aria-label="HD.cafe">
        <button type="button" className="auth_close" onClick={close} aria-label="Close">
          <div className="t_icon t_icon--20"><div>close</div></div>
        </button>

        <div className="auth_logo">
          <Logo />
        </div>
        {(isForgot) && (
          <div className="auth_title" id="auth_card_title">{title}</div>
        )}

        {!isForgot && (
          <div className="auth_tabs">
            <button
              type="button"
              className={`auth_tab ${mode === AUTH_MODAL_LOGIN ? 'active' : ''}`}
              onClick={() => setMode(AUTH_MODAL_LOGIN)}
            >
              {$ui('auth_tab_login')}
            </button>
            <button
              type="button"
              className={`auth_tab ${isSignup ? 'active' : ''}`}
              onClick={() => setMode(AUTH_MODAL_SIGNUP)}
            >
              {$ui('auth_tab_signup')}
            </button>
          </div>
        )}

        {!isForgot && (
          <div className="auth_google_bar">
            <a className="auth_google" href={googleAuthHref(mode)}>
              <GoogleMark />
              {isSignup ? $ui('auth_google_signup') : $ui('auth_google_signin')}
            </a>
            <a className="auth_google" href={appleAuthHref(mode)}>
              <AppleMark />
              {isSignup ? $ui('auth_apple_signup') : $ui('auth_apple_signin')}
            </a>
          </div>
        )}

        <form className="auth_form" onSubmit={submit}>
          {!isForgot && <div className="auth_or">{$ui('auth_or')}</div>}

          <label className="auth_field">
            <span className="t_icon t_icon--20 auth_field_ico"><span>mail</span></span>
            <input
              className="auth_input"
              type="email"
              autoComplete="email"
              placeholder={$ui('auth_email_placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          {!isForgot && (
            <label className="auth_field">
              <span className="t_icon t_icon--20 auth_field_ico"><span>lock</span></span>
              <input
                className="auth_input"
                type="password"
                autoComplete={isSignup ? 'new-password' : 'current-password'}
                placeholder={$ui('auth_password_placeholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
          )}

          {isSignup && (
            <label className="auth_field">
              <span className="t_icon t_icon--20 auth_field_ico"><span>lock</span></span>
              <input
                className="auth_input"
                type="password"
                autoComplete="new-password"
                placeholder={$ui('auth_password_repeat')}
                value={passwordRepeat}
                onChange={(e) => setPasswordRepeat(e.target.value)}
              />
            </label>
          )}

          {mode === AUTH_MODAL_LOGIN && (
            <button
              type="button"
              className="auth_forgot_link"
              onClick={() => setMode(AUTH_MODAL_FORGOT)}
            >
              {$ui('auth_forgot')}
            </button>
          )}

          {isForgot && forgotSent && (
            <div className="auth_note">{$ui('auth_forgot_sent')}</div>
          )}


          {/* //TODO: add terms and conditions */}
          {/* {isSignup && (
            <div className="auth_note">{$ui('auth_terms')}</div>
          )} */}

          {error && <div className="auth_error">{error}</div>}

          <button type="submit" className="auth_submit" disabled={busy}>
            {isForgot
              ? $ui('auth_forgot_send')
              : isSignup
                ? $ui('auth_tab_signup')
                : $ui('auth_tab_login')}
          </button>

          {isForgot && (
            <button
              type="button"
              className="auth_forgot_link"
              onClick={() => setMode(AUTH_MODAL_LOGIN)}
            >
              {$ui('auth_forgot_back')}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
